import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Box, Typography, Checkbox, Button, Alert, Snackbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DownloadIcon from '@mui/icons-material/Download';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import InitiateRequestDialog from '../../components/books/InitiateRequestDialog';
import { getSavedBooksApi, removeSavedBooksApi, exportSavedBooksApi } from '../../api/books';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function SavedBooks() {
  const [savedBooks, setSavedBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState({ message: '', severity: 'success' });

  const fetchSavedBooks = useCallback(async () => {
    const res = await getSavedBooksApi({ page: page + 1, page_size: rowsPerPage });
    const results = res.data.results || res.data;
    setSavedBooks(results);
    setTotal(res.data.count ?? results.length);
    setSelected([]);
  }, [page, rowsPerPage]);

  useEffect(() => { fetchSavedBooks(); }, [fetchSavedBooks]);

  const books = savedBooks.map((s) => s.book);

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === books.length) setSelected([]);
    else setSelected(books.map((b) => b.id));
  };

  const handleRemoveSelected = async () => {
    setRemoving(true);
    try {
      const res = await removeSavedBooksApi(selected);
      setSnackbar({ message: res.data.message || 'Book(s) removed from saved list.', severity: 'success' });
      fetchSavedBooks();
    } catch (err) {
      const data = err.response?.data;
      setSnackbar({ message: (data && data.error) || 'Failed to remove books.', severity: 'error' });
    } finally {
      setRemoving(false);
    }
  };

  const handleRequestSuccess = (message) => {
    setRequestDialogOpen(false);
    setSnackbar({ message, severity: 'success' });
    fetchSavedBooks();
  };

  const handleExport = async (fileFormat) => {
    try {
      const res = await exportSavedBooksApi({ export_format: fileFormat });
      downloadBlob(res.data, `saved_books.${fileFormat}`);
    } catch (err) {
      setSnackbar({ message: 'Failed to export saved books.', severity: 'error' });
    }
  };

  return (
    <Layout title="Saved Books">
      <PageHeader
        title="Saved Books"
        breadcrumbs={[{ label: 'Saved Books' }]}
        action={
          total > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('csv')}>Export CSV</Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('xlsx')}>Export Excel</Button>
            </Box>
          )
        }
      />
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>My Saved List</Typography>
            {selected.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleRemoveSelected}
                  disabled={removing}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {removing ? 'Removing...' : `Remove Selected (${selected.length})`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setRequestDialogOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Initiate Request ({selected.length})
                </Button>
              </Box>
            )}
          </Box>

          {books.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <BookmarkBorderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                You haven't saved any books yet. Browse books and click "Save for Later" to build your list here.
              </Typography>
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < books.length}
                        checked={books.length > 0 && selected.length === books.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>ISBN</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Publisher</TableCell>
                    <TableCell>Language</TableCell>
                    <TableCell>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {books.map((b) => (
                    <TableRow key={b.id} hover selected={selected.includes(b.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.includes(b.id)} onChange={() => toggleSelect(b.id)} />
                      </TableCell>
                      <TableCell>{b.isbn}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</TableCell>
                      <TableCell>{b.author_name}</TableCell>
                      <TableCell>{b.publisher_company || b.publisher_name}</TableCell>
                      <TableCell>{b.language}</TableCell>
                      <TableCell>₹{b.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]} />
            </>
          )}
        </CardContent>
      </Card>

      <InitiateRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        selectedIds={selected}
        onSuccess={handleRequestSuccess}
      />

      <Snackbar open={!!snackbar.message} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, message: '' })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, message: '' })}>{snackbar.message}</Alert>
      </Snackbar>
    </Layout>
  );
}
